import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Swiper from 'swiper';
import swiperCss from '@edikk-edikk/country-frontend/css/modules/swiper.module.scss';
import { useChangeTimer, useProgressInterval, UseInitResultType } from '#advertStory/detail-list';
import { useStoryListContext } from '#advertStory/list';
import { STORY_DETAIL_LIST_DURATION } from '#advertStory/constants';

const useInit = (
  refSwiper: RefObject<HTMLDivElement>,
  initialSlide: number,
): UseInitResultType => {
  const { storyDataList } = useStoryListContext();
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlide);
  const refIsPause = useRef<boolean>(false);
  const refSwiperInstance = useRef<Swiper>(null);
  const { stopChangeTimer, startChangeTimer } = useChangeTimer(refSwiperInstance);
  const { progress, stopProgressInterval, startProgressInterval } = useProgressInterval();

  const pause = useCallback((): void => {
    refIsPause.current = true;
    stopProgressInterval(false);
    stopChangeTimer();
  }, [stopChangeTimer, stopProgressInterval]);

  const resume = useCallback((): void => {
    refIsPause.current = false;
    startChangeTimer(STORY_DETAIL_LIST_DURATION - progress);
    startProgressInterval(false);
  }, [progress, startChangeTimer, startProgressInterval]);

  const handlerSlideChange = useCallback((swiper: Swiper): void => {
    if (refIsPause.current) {
      return;
    }

    setCurrentSlide(swiper.realIndex);
    startChangeTimer();
    startProgressInterval();
  }, [startChangeTimer, startProgressInterval]);

  useEffect(() => {
    if (refSwiperInstance.current) {
      return;
    }

    refSwiperInstance.current = new Swiper(refSwiper.current, {
      loop: storyDataList.length > 1,
      initialSlide: currentSlide,
      on: {
        slideChange: handlerSlideChange,
        touchStart: pause,
        touchEnd: resume,
      },
      wrapperClass: swiperCss.swiper__wrapper,
      slideClass: swiperCss.swiper__slide,
    });
  }, [currentSlide, handlerSlideChange, pause, refSwiper, resume, storyDataList.length]);

  return {
    progress,
    currentSlide,
  };
};

export { useInit };
